<template>
    <v-container fluid v-if="sessionActive">

        <v-card class="pt-1" v-if="hasPermission(minPermission)">

            <material-header
                title="Clients"
                info="Manage clients"
                help="clients"
                icon="mdi-card-account-details-outline">
                <template slot="buttons">

                    <!-- REFRESH -->
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission(minPermission)"
                                   @click="refreshClicked()"
                                   v-on="on"
                                   icon>
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Reload data</span>
                    </v-tooltip>

                    <!-- CREATE NEW -->
                    <v-tooltip :color="hasPermission('create clients') ? 'primary' : 'red'" top>
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission('create projects')"
                                   v-on="on"
                                   fab
                                   small
                                   class="elevation-0"
                                   color="primary"
                                   @click="newClicked()">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        <span v-if="hasPermission('create clients')">Create new source</span>
                        <span v-else>You are not authorized to create new recruitment sources</span>
                    </v-tooltip>

                </template>

            </material-header>

            <v-row v-if="!hasPermission(minPermission)">
                <v-col cols="12">
                    <v-alert type="error" class="body-2">
                        Your user account doesn't have sufficient permissions for this feature
                    </v-alert>
                </v-col>
            </v-row>

            <v-row v-show="clients.length>0" class="pt-0">

                <v-col sm="4" offset-sm="8">

                    <v-text-field
                        class="pr-4 pt-2 mb-2"
                        v-model="search"
                        append-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        solo
                        dense
                        background-color="grey darken-2"
                    ></v-text-field>
                </v-col>

            </v-row>

            <v-data-table
                no-results-text="No clients found. Try a different search criteria."
                no-data-text="No clients found!"
                :loading="clientsLoading"
                :headers="headers"
                :items="clients"
                :search="search"
                :footer-props="{itemsPerPageOptions:[10,25,50,-1]}"
                sort-by="name"
                :sort-desc="false"
            >
                <template v-slot:body="{ items }">
                    <tbody>

                    <!-- NO CLIENT FOUND -->
                    <tr v-if="items.length===0 && !search && !clientsLoading">
                        <td colspan="5">
                            <v-alert type="info" class="ma-5 body-2">
                                No clients found! To add a new client click on the add button top right corner.
                                Sample Ninja can segment your reports by client to give you better insights.
                                For this reason we always recommended associating a client with each project you run.
                            </v-alert>
                        </td>
                    </tr>

                    <!-- SEARCH NO CLIENTS FOUND -->
                    <tr v-if="items.length===0 && search && !clientsLoading">
                        <td colspan="5">
                            <v-alert type="info" class="ma-5 body-2">
                                No clients found with the given search criteria
                            </v-alert>
                        </td>
                    </tr>

                    <!-- ITEMS -->
                    <tr v-for="item in items" :key="item.id" @click="editClicked(item)" class="cursor-pointer">

                        <td class="text-left">
                            <v-chip>{{item.id}}</v-chip>
                        </td>

                        <td class="cursor-pointer">{{ item.name }}

                        </td>

                        <td>
                            <div style="white-space: nowrap;">
                                {{ item.primary_contact_name }}
                            </div>
                        </td>

                        <td>
                            <div style="white-space: nowrap;">
                                {{ item.primary_contact_email }}
                            </div>
                        </td>

                        <td>
                            <div style="white-space: nowrap;">
                                <v-icon class="pr-1" small>mdi-phone</v-icon>
                                {{ item.primary_contact_phone }}
                            </div>
                        </td>

                        <td class="text-left pa-0">
                            <div style="white-space: nowrap;">

                                <!-- EDIT BUTTON -->
                                <v-tooltip top color="primary">
                                    <template v-slot:activator="{ on }">
                                        <v-btn :disabled="!hasPermission('view clients')"
                                               class="ma-0"
                                               icon
                                               color="primary"
                                               v-on="on"
                                               @click.stop="editClicked(item)">

                                            <v-icon light small color="white">mdi-cog</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit client details</span>
                                </v-tooltip>

                                <!-- DELETE BUTTON -->
                                <v-tooltip top color="primary">
                                    <template v-slot:activator="{ on }">
                                        <v-btn :disabled="!hasPermission('delete clients')"
                                               class="ma-0"
                                               icon
                                               text
                                               v-on="on"
                                               :loading="deleting"
                                               @click.stop="deleteClicked(item.id)"
                                               color="secondary">
                                            <v-icon light small color="white">mdi-delete</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Delete client</span>
                                </v-tooltip>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </template>

            </v-data-table>

        </v-card>

        <edit-client-dialog
            v-if="client"
            @change="clientModified"
            v-model="editDialogModel"
            :client="client"
            :saving="saving"
            :key="editDialogKey"
        ></edit-client-dialog>

    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {progress} from '../../../components/mixins/progressMixin';
import EditClientDialog from './EditClientDialog';
import {v4 as uuid} from "uuid";

const vuexModule = 'Clients';

export default {

    mixins: [activeSessionChecker, progress],

    components: {
        Json,
        MaterialHeader,
        EditClientDialog
    },

    data() {
        return {
            minPermission: 'view clients',
            search: '',
            headers: [
                {
                    text: '',
                    align: 'left',
                    sortable: true,
                    value: 'id',
                    width: 50
                },
                {
                    text: 'NAME',
                    align: 'left',
                    sortable: true,
                    value: 'name',
                    width: 300
                },
                {text: 'PRIMARY CONTACT', value: 'contact_name'},
                {text: 'EMAIL', value: 'contact_email'},
                {text: 'PHONE', value: 'contact_phone', sortable: false},
                {test: 'ACTIONS', width: '80px', sortable: false}
            ],
            client: null,
            editDialogModel: false,
            editDialogKey: uuid(),
            saving: false,
            highlighted: null,
            deleting: false,
        }
    },
    computed: {

        ...mapState(vuexModule, [
            'clients',
            'clientsLoading'
        ]),
    },

    methods: {
        ...mapActions(vuexModule, [
            'getClients',
            'createClient',
            'updateClient',
            'deleteClient'
        ]),

        newClicked() {
            this.client = {
                name: '',
                notes: '',
                primary_contact_name: '',
                primary_contact_email: '',
                primary_contact_phone: '',
                secondary_contact_name: '',
                secondary_contact_email: '',
                secondary_contact_phone: '',
            };
            this.editDialogKey = uuid();
            this.editDialogModel = true;
        },

        editClicked(client) {
            this.client = Object.assign({}, client);
            this.editDialogModel = true;
        },

        refreshClicked() {
            this.loadClients(true);
        },

        deleteClicked(id) {
            this.deleting = true;

            this.deleteClient(id)
                .then(() => {
                    Bus.$emit('flash-message', {type: 'info', message: 'Client successfully deleted!'});
                })
                .catch((error) => {

                    if (error.response.status === 409) {
                        Bus.$emit('flash-message', {type: 'error', message: error.response.data.message});
                    } else {
                        Bus.$emit('flash-message', {type: 'error', message: 'OOPS! We failed to delete the client! Please try again!'});
                    }
                })
                .finally(() => {
                    this.deleting = false;
                })
        },

        loadClients(force) {
            this.getClients(force)
                .then(() => {
                    if (force) {
                        Bus.$emit('flash-message', {type: 'info', message: 'Client list is now updated!'});
                    }
                })
                .catch((error) => {
                    Bus.$emit('flash-message', {type: 'error', message: 'Clients failed to load!'});
                });
        },

        clientModified(client) {
            this.saving = true;

            if (client.id) {
                this.updateClient(client)
                    .then(() => {
                        Bus.$emit('flash-message', {type: 'info', message: 'Client updated!'});
                        this.saving = false;
                        this.editDialogModel = false;
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Update error. Please verify that client\'s name is unique'
                        });
                        this.saving = false;
                    });
            } else {
                this.createClient(client)
                    .then(() => {
                        Bus.$emit('flash-message', {type: 'info', message: 'Client created!'});
                        this.saving = false;
                        this.editDialogModel = false;
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Oops. Could create client. Please make sure client\'s name is unique'
                        });
                        this.saving = false;
                    });
            }
        },
    },

    mounted() {
        this.loadClients(false);
    },
}
</script>
